const R = require('ramda')
const ordinal = require('ordinal-suffix')

const ANNUAL_INCREASE_NO_DEGREE = 0.005
const ANNUAL_INCREASE_DEGREE = 0.03
const TAX_RATE_NO_DEGREE = 0.05
const TAX_RATE_DEGREE = 0.15

/**
 * @const calculatePayment
 * @description Calculates a single payment
 * @param principal
 * @param interestRate
 * @param numPayments
 */
const calculatePayment = (principal, interestRate, numPayments) =>
  principal *
  interestRate *
  (1 + interestRate) ** numPayments /
  ((1 + interestRate) ** numPayments - 1)

/**
 * @const makePayment
 * @description Makes a single recurring payment
 * @param principal
 * @param interestRate
 * @param paymentAmount
 * @returns {{principal: *, interestPayment: number, principalPayment: number}}
 */
const makePayment = (principal, interestRate, paymentAmount) => {
  const interest = principal * interestRate
  let payment = paymentAmount
  let newPrincipal = principal + interest
  if (newPrincipal < payment) {
    payment = newPrincipal
  }
  newPrincipal -= payment

  return {
    principal: newPrincipal,
    interestPayment: interest,
    principalPayment: payment - interest,
  }
}

/**
 * @const makeYearOfPayments
 * @description Make 12 recurring payments
 * @param principal
 * @param monthlyInterestRate
 * @param monthlyPayment
 * @returns {{startingPrincipal: *, endingPrincipal: Number, interestPayment: Number, principalPayment: Number}}
 */
const makeYearOfPayments = (principal, monthlyInterestRate, monthlyPayment) => {
  let newPrincipal = principal
  let interestPayment = 0
  let principalPayment = 0

  for (let m = 0; m < 12; m += 1) {
    const result = makePayment(
      newPrincipal,
      monthlyInterestRate,
      monthlyPayment,
    )
    interestPayment += result.interestPayment
    principalPayment += result.principalPayment
    newPrincipal = result.principal
  }

  return {
    startingPrincipal: principal,
    endingPrincipal: parseFloat(Math.max(newPrincipal, 0)),
    interestPayment: parseFloat(interestPayment),
    principalPayment: parseFloat(principalPayment),
  }
}

/**
 * @const amortize
 * @param principal
 * @param termInYears
 * @param annualInterest
 * @returns {{termInYears: *, annualInterest: *, monthlyPayment, totalInterestPaid, totalPrincipalPaid, paymentBreakdown, totalPaid: Number}}
 */
const amortize = (principal, termInYears, annualInterest) => {
  const monthlyInterestRate = annualInterest / 12
  const numPayments = termInYears * 12
  const monthlyPayment = calculatePayment(
    principal,
    monthlyInterestRate,
    numPayments,
  )

  let P = principal

  const paymentBreakdown = R.range(1, termInYears + 1).map(item => {
    const yearData = makeYearOfPayments(P, monthlyInterestRate, monthlyPayment)
    yearData.y = item
    P = yearData.endingPrincipal
    return yearData
  })

  const totalInterestPaid = paymentBreakdown.reduce(
    (acc, item) => acc + item.interestPayment,
    0,
  )
  const totalPrincipalPaid = paymentBreakdown.reduce(
    (acc, item) => acc + item.principalPayment,
    0,
  )

  return {
    termInYears,
    annualInterest,
    monthlyPayment,
    totalInterestPaid,
    totalPrincipalPaid,
    paymentBreakdown,
    totalPaid: parseFloat(monthlyPayment * numPayments),
  }
}

/**
 * @const summarizeAmortizationChartData
 * @param amortizationResult
 * @param breakevenResult
 * @returns {{analysis: {subvalues}}}
 */
const summarizeAmortizationChartData = (
  amortizationResult,
  breakevenResult,
) => {
  const subvalues = amortizationResult.paymentBreakdown.map((item, index) => ({
    beggining_balance: item.startingPrincipal,
    interest_paid: parseFloat(item.interestPayment),
    name: `YR${1 + index}`,
    principal_paid: parseFloat(item.principalPayment),
    total_investment: parseFloat(amortizationResult.totalPaid),
    value: parseFloat(item.startingPrincipal + item.interestPayment),
    cumm: breakevenResult.analysis[index].cumm,
    end_salary_no_degree: breakevenResult.analysis[index].st_salary_no_degree,
    end_salary_w_degree: breakevenResult.analysis[index].st_salary_w_degree,
    incremental_diff_dge_no_deg:
      breakevenResult.analysis[index].incremental_diff_dge_no_deg,
  }))

  return {
    analysis: {
      subvalues,
    },
  }
}

const summarizeBreakeven = (amortizationResult, breakevenResult) => {
  let breakevenYearData = null
  const analysis = breakevenResult.analysis
    .map((item, index) => {
      if (item.breakeven_year === 'Yes') {
        breakevenYearData = item
        return {
          breakeven_year: index + 1,
          cumm: breakevenYearData.cumm,
          incremental_diff_dge_no_deg:
            breakevenYearData.incremental_diff_dge_no_deg,
          loan_amount: amortizationResult.totalPrincipalPaid,
          loan_payment: amortizationResult.monthlyPayment,
          loan_rate: amortizationResult.annualInterest,
          loan_term_year: amortizationResult.termInYears,
          monthly_incremental_diff_dge_no_deg:
            breakevenYearData.monthly_incremental_diff_dge_no_deg,
          st_salary_no_degree: breakevenYearData.st_salary_no_degree,
          st_salary_w_degree: breakevenYearData.st_salary_w_degree,
          total_interest_paid_on_loan: amortizationResult.totalInterestPaid,
          total_investment: parseFloat(amortizationResult.totalPaid),
          worthit: 1,
        }
      }
      return null
    })
    .filter(item => item)
  return { analysis }
}

/**
 * @const analyzeBreakeven
 * @param amortizationResult
 * @param startSalaryNoDegree
 * @param startSalaryWithDegree
 * @returns {{analysis}}
 */
const analyzeBreakeven = (
  amortizationResult,
  startSalaryWithDegree,
  startSalaryNoDegree,
) => {
  const monthlyPayment = amortizationResult.monthlyPayment
  let cumm = 0
  let current_salary_no_degree = startSalaryNoDegree
  let current_salary_with_degree = startSalaryWithDegree
  let remainingCost = amortizationResult.totalPaid
  let breakeven_flag = 'No'

  const analysis = R.range(1, 31).map((item) => {
    const paymentThisYear =
      remainingCost > 12 * monthlyPayment ? 12 * monthlyPayment : remainingCost
    const net_income_with_degree = current_salary_with_degree * (1 - TAX_RATE_DEGREE) - paymentThisYear
    const net_income_without_degree = current_salary_no_degree * (1 - TAX_RATE_NO_DEGREE)
    const incomeAdvantageForYear = net_income_with_degree - net_income_without_degree

    remainingCost = Math.max(remainingCost - incomeAdvantageForYear, 0)

    if (remainingCost <= 0) {
      breakeven_flag = 'Yes'
    }

    cumm += incomeAdvantageForYear

    const yearlyResult = {
      period: item,
      breakeven_year: breakeven_flag,
      loan_amount: amortizationResult.totalPrincipalPaid,
      loan_term_years: amortizationResult.termInYears,
      loan_rate: amortizationResult.annualInterest,
      cumm: parseFloat(cumm),
      incremental_diff_dge_no_deg: parseInt(incomeAdvantageForYear, 10),
      monthly_incremental_diff_dge_no_deg: parseInt(
        incomeAdvantageForYear / 12,
        10,
      ),
      st_salary_no_degree: Math.round(current_salary_no_degree),
      st_salary_w_degree: Math.round(current_salary_with_degree),
    }

    current_salary_no_degree *=
      (1 + ANNUAL_INCREASE_NO_DEGREE)
    current_salary_with_degree *=
      (1 + ANNUAL_INCREASE_DEGREE)

    return yearlyResult
  })

  return { analysis }
}

/**
 * @const getLoanInfo
 * @description Renamer
 * @param amortizationResult
 */
const getLoanInfo = amortizationResult => ({
  analysis: [
    {
      loan_amount: amortizationResult.totalPrincipalPaid,
      loan_payment: amortizationResult.monthlyPayment,
      loan_rate: amortizationResult.annualInterest,
      loan_term: amortizationResult.termInYears,
      total_interest_paid_on_loan: amortizationResult.totalInterestPaid,
      total_investment: amortizationResult.totalPaid,
    },
  ],
})


/**
 * @const balanceByYear
 * @param amortizationResult
 * @returns {{analysis}}
 */
const balanceByYear = amortizationResult => {
  const analysis = amortizationResult.paymentBreakdown.map((item, index) => ({
      yr: index + 1,
      rate: amortizationResult.annualInterest,
      payment: amortizationResult.monthlyPayment,
      beggining_balance: item.startingPrincipal,
      ending_balance: item.endingPrincipal,
      interest_paid: item.interestPayment,
      principal_paid: item.principalPayment,
      total_annual_payment: amortizationResult.totalPaid,
      loanyrs: `${ordinal(index + 1)} Year`,
    })
  )
  return { analysis }
}


const formatAmortizationChartData = (amortizationResult, startSalaryWithDegree, startSalaryNoDegree) => {
  const rows = []
  const breakevenResult = analyzeBreakeven(amortizationResult, startSalaryWithDegree, startSalaryNoDegree)
  rows[0] = balanceByYear(amortizationResult)
  rows[1] = getLoanInfo(amortizationResult)
  rows[2] = breakevenResult
  rows[3] = summarizeBreakeven(amortizationResult, breakevenResult)
  rows[4] = summarizeAmortizationChartData(amortizationResult, breakevenResult)
  return rows
}

export default {
  amortize,
  formatAmortizationChartData,
}



// WEBPACK FOOTER //
// ./src/utils/amortization/index.js